// feature/contestaciones-validation — validación de contestación. Mirrors Carlos Morillo's MARZO
// Excel: FECHA, RADICADO, NOMBRE, SAMAI/Contestacion (via), OBSERVACIONES,
// REVISION EXTERNA (rebote tracking).
import React, { useEffect, useState } from "react";
import { contestaciones as api, users as usersApi } from "../api/endpoints";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import Spinner from "../components/Spinner";
import Empty from "../components/Empty";
import Modal from "../components/Modal";
import { rolesOf } from "../utils/permissions";

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Contestaciones() {
  const { user } = useAuth();
  const toast = useToast();
  // feature/single-role-audit — admin OR directora via multi-role.
  const isAdmin = ["admin", "directora"].some(r => rolesOf(user).includes(r));
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ mes: "", abogado_id: "", via: "", validada: "" });
  const [usersList, setUsersList] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [billingMes, setBillingMes] = useState(thisMonth());

  function load() {
    setData(null);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.list(params).then(r => setData(r.data || [])).catch(() => setData([]));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);
  useEffect(() => { usersApi.list().then(r => setUsersList(r.data || [])); }, []);

  function loadBilling() {
    api.billing(billingMes).then(r => setBilling(r.data || [])).catch(() => setBilling([]));
  }
  useEffect(() => { if (isAdmin) loadBilling(); /* eslint-disable-next-line */ }, [billingMes, isAdmin]);

  async function toggleValidada(c) {
    try {
      await api.update(c.id, { validada: !c.validada });
      toast.push(c.validada ? "Marcada como no validada" : "Validada");
      load();
    } catch (e) { toast.push(e?.response?.data?.error || "Error", "error"); }
  }
  async function toggleRebote(c) {
    try {
      await api.update(c.id, { rebote_detectado: !c.rebote_detectado });
      toast.push(c.rebote_detectado ? "Rebote desmarcado" : "Rebote marcado · abogado notificado");
      load();
    } catch (e) { toast.push(e?.response?.data?.error || "Error", "error"); }
  }

  // feature/single-role-audit — FALSE POSITIVE: list filter on user.role
  // (PRIMARY role only). Every active abogado has primary 'abogado'
  // today. See same-pattern comment in CaseDetail.js.
  const abogados = usersList.filter(u => u.role === "abogado");

  return (
    <div className="col" style={{ gap: 24 }}>
      <div className="row" style={{ alignItems: "center" }}>
        <div>
          <h1 style={{ margin: 0 }}>Validación de contestación</h1>
          <div className="muted" style={{ marginTop: 4 }}>
            Registro mensual basado en el formato de Carlos Morillo. Marque validadas y rebotes.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreateOpen(true)}>+ Registrar contestación</button>
      </div>

      <div className="card tight" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div>
          <label>Mes</label>
          <input type="month" value={filters.mes} onChange={(e) => setFilters({ ...filters, mes: e.target.value })} />
        </div>
        <div>
          <label>Abogado</label>
          <select value={filters.abogado_id} onChange={(e) => setFilters({ ...filters, abogado_id: e.target.value })}>
            <option value="">Todos</option>
            {abogados.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label>Vía</label>
          <select value={filters.via} onChange={(e) => setFilters({ ...filters, via: e.target.value })}>
            <option value="">Todas</option>
            <option value="samai">SAMAI</option>
            <option value="correo">Correo</option>
          </select>
        </div>
        <div>
          <label>Validada</label>
          <select value={filters.validada} onChange={(e) => setFilters({ ...filters, validada: e.target.value })}>
            <option value="">Todas</option>
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {data == null ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
        ) : !data.length ? (
          <Empty title="Sin contestaciones registradas" message="Registre la primera con el botón superior." />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Fecha</th><th>Radicado</th><th>Accionante</th>
                <th>Tipo de actuación</th><th>Vía</th><th>Abogado</th>
                <th>Validada</th><th>Rebote</th><th>Observaciones</th>
              </tr>
            </thead>
            <tbody>
              {data.map(c => (
                <tr key={c.id} style={c.rebote_detectado ? { background: "var(--color-danger-bg)" } : {}}>
                  <td>{c.fecha_contestacion ? new Date(c.fecha_contestacion).toLocaleDateString("es-CO") : "—"}</td>
                  <td>{c.radicado || "—"}</td>
                  <td>{c.nombre_accionante || "—"}</td>
                  <td>{c.tipo_actuacion || "—"}</td>
                  <td>{c.via || "—"}</td>
                  <td>{c.abogado_name || "—"}</td>
                  <td>
                    {c.validada ? "✓" : "—"}
                    {isAdmin && (
                      <button className="ghost sm" style={{ marginLeft: 4 }} onClick={() => toggleValidada(c)}>
                        {c.validada ? "Quitar" : "Validar"}
                      </button>
                    )}
                  </td>
                  <td>
                    {c.rebote_detectado ? <span style={{ color: "var(--color-danger)", fontWeight: 600 }}>Rebote</span> : "—"}
                    {isAdmin && (
                      <button className="ghost sm" style={{ marginLeft: 4 }} onClick={() => toggleRebote(c)}>
                        {c.rebote_detectado ? "Quitar" : "Marcar"}
                      </button>
                    )}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{c.observaciones || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isAdmin && (
        <div className="glass-section">
          <div className="row" style={{ alignItems: "center" }}>
            <h3>Contestaciones por abogado</h3>
            <div style={{ flex: 1 }} />
            <input type="month" value={billingMes} onChange={(e) => setBillingMes(e.target.value)} />
          </div>
          {billing == null ? (
            <Spinner />
          ) : !billing.length ? (
            <p className="muted">Sin contestaciones en {billingMes}.</p>
          ) : (
            <table>
              <thead><tr><th>Abogado</th><th>Total</th><th>Validadas</th><th>Rebotes</th></tr></thead>
              <tbody>
                {billing.map(r => (
                  <tr key={r.id}>
                    <td>{r.name}</td>
                    <td>{r.total}</td>
                    <td>{r.validadas}</td>
                    <td>{r.rebotes > 0 ? <span style={{ color: "var(--color-danger)" }}>{r.rebotes}</span> : 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {createOpen && (
        <CreateModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} abogados={abogados} />
      )}
    </div>
  );
}

function CreateModal({ open, onClose, onCreated, abogados }) {
  const toast = useToast();
  const [form, setForm] = useState({
    fecha_contestacion: new Date().toISOString().slice(0, 10),
    radicado: "", nombre_accionante: "", tipo_actuacion: "Contestacion",
    via: "samai", observaciones: "", abogado_id: "",
  });
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      await api.create({
        ...form,
        abogado_id: form.abogado_id || null,
      });
      toast.push("Contestación registrada");
      onCreated?.();
      onClose();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error", "error");
    } finally { setBusy(false); }
  }

  return (
    <Modal
      open={open} title="Registrar contestación"
      onClose={busy ? undefined : onClose}
      footer={<>
        <button className="secondary" disabled={busy} onClick={onClose}>Cancelar</button>
        <button disabled={busy} onClick={submit}>{busy && <Spinner color="currentColor" />} Registrar</button>
      </>}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label>Fecha</label>
          <input type="date" value={form.fecha_contestacion} onChange={(e) => setForm({ ...form, fecha_contestacion: e.target.value })} />
        </div>
        <div>
          <label>Vía</label>
          <select value={form.via} onChange={(e) => setForm({ ...form, via: e.target.value })}>
            <option value="samai">SAMAI</option>
            <option value="correo">Correo</option>
          </select>
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label>Radicado</label>
          <input value={form.radicado} onChange={(e) => setForm({ ...form, radicado: e.target.value })} />
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label>Nombre del accionante</label>
          <input value={form.nombre_accionante} onChange={(e) => setForm({ ...form, nombre_accionante: e.target.value })} />
        </div>
        <div>
          <label>Tipo de actuación</label>
          <input value={form.tipo_actuacion} onChange={(e) => setForm({ ...form, tipo_actuacion: e.target.value })} />
        </div>
        <div>
          <label>Abogado</label>
          <select value={form.abogado_id} onChange={(e) => setForm({ ...form, abogado_id: e.target.value })}>
            <option value="">— elegir —</option>
            {abogados.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label>Observaciones</label>
          <textarea value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
        </div>
      </div>
    </Modal>
  );
}
